import { Fragment } from "react";
import { motion, useReducedMotion } from "framer-motion";

const COMPETITORS = [
  { name: "Claude Code", short: "Claude", logo: "/images/competitors/claude-code.webp" },
  { name: "Codex", short: "Codex", logo: "/images/competitors/codex.webp" },
  { name: "OpenCode", short: "OpenC.", logo: "/images/competitors/opencode.webp" },
  { name: "T3 Code", short: "T3", logo: "/images/competitors/t3-code.webp" },
  { name: "Cursor", short: "Cursor", logo: "/images/competitors/cursor.webp" },
  { name: "Superset", short: "Superset", logo: "/images/competitors/superset.webp" },
  { name: "Conductor", short: "Cond.", logo: "/images/competitors/conductor.webp" },
  { name: "Factory", short: "Factory", logo: "/images/competitors/factory.webp" },
  { name: "Paperclip", short: "Paperc.", logo: "/images/competitors/paperclip.webp" },
  { name: "GitHub", short: "GitHub", logo: "/images/competitors/github.webp" },
] as const;

/**
 * Single-row competitor equation: tools + equals + ADE dock icon.
 */
export function CompetitorEquation() {
  const reduceMotion = useReducedMotion() ?? true;

  const stagger = 0.065;
  const rowChildCount = COMPETITORS.length * 2 - 1;
  const rowEnd = 0.1 + rowChildCount * stagger;

  const container = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: reduceMotion ? 0 : stagger,
        delayChildren: reduceMotion ? 0 : 0.1,
      },
    },
  };
  const item = {
    hidden: reduceMotion ? {} : { opacity: 0, y: 14, scale: 0.85 },
    show: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
    },
  };

  return (
    <div className="flex flex-wrap items-center justify-center gap-2 px-2 pt-2 pb-0 sm:gap-3 sm:px-4 sm:pt-3">
      <motion.div
        variants={container}
        initial="hidden"
        animate="show"
        className="flex max-w-full flex-wrap items-center justify-center gap-1 overflow-x-auto px-2 sm:gap-1.5 sm:px-4"
      >
        {COMPETITORS.map((app, i) => (
          <Fragment key={app.name}>
            {i > 0 && (
              <motion.span
                aria-hidden="true"
                variants={item}
                className="shrink-0 font-sans text-[14px] text-white/25 sm:text-[15px]"
              >
                +
              </motion.span>
            )}
            <motion.div variants={item} className="flex shrink-0 flex-col items-center gap-[3px]">
              <div className="group/logo flex h-7 w-7 items-center justify-center overflow-hidden rounded-[7px] border border-[color:var(--color-hairline-strong)] bg-white/[0.045] p-[3px] transition-all duration-300 motion-safe:hover:-translate-y-[2px] hover:border-[color:var(--color-violet-bright)]/55 sm:h-8 sm:w-8">
                <img
                  src={app.logo}
                  alt={app.name}
                  className="h-full w-full object-contain"
                />
              </div>
              <span aria-hidden="true" className="text-[8px] uppercase tracking-[0.08em] text-[color:var(--color-cream-muted)]">
                {app.short}
              </span>
            </motion.div>
          </Fragment>
        ))}
      </motion.div>

      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 12 }}
        animate={reduceMotion ? undefined : { opacity: 1, y: 0 }}
        transition={{
          duration: 0.7,
          delay: reduceMotion ? 0 : rowEnd + 0.05,
          ease: [0.22, 1, 0.36, 1],
        }}
        className="relative flex shrink-0 items-center gap-3 sm:gap-4"
      >
        <span
          className="font-sans font-medium tracking-[-0.02em] text-white/45"
          style={{ fontSize: "clamp(15px, 1.4vw, 18px)" }}
        >
          equals
        </span>

        <div className="relative">
          {!reduceMotion && (
            <motion.div
              aria-hidden
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: [0, 0.65, 0],
                scale: [0.8, 1.3, 1.7],
              }}
              transition={{
                duration: 1.6,
                delay: rowEnd + 0.4,
                ease: "easeOut",
              }}
              style={{
                width: "160px",
                height: "160px",
                background: "radial-gradient(circle, rgba(124,58,237,0.55) 0%, transparent 65%)",
              }}
            />
          )}
          <img
            src="/images/ade-dock-icon.webp"
            alt="ADE"
            className="relative h-[52px] w-[52px] rounded-[22%] object-contain drop-shadow-[0_10px_36px_rgba(124,58,237,0.55)] sm:h-[60px] sm:w-[60px]"
          />
        </div>
      </motion.div>
    </div>
  );
}
